import { Component, h, Host, Prop, Element } from "@stencil/core";

export type ParaFlap = {
  cardData: string;
  title: string;
  subTitle: string;
  titleSize: string;
  titleColor: "white" | "navy";
  subTitleSize: string;
  subTitleColor: "white" | "navy";
};

@Component({
  tag: "scs-paraflap",
  styleUrls: ["paraflap.scss"],
  shadow: true,
})
export class Paraflap {
  @Prop() public heroCardData: string = "[]";

  @Prop() public flapTitle?: string;
  @Prop() public flapSubTitle?: string;

  @Prop() public flapTitleSize?: string;
  @Prop() public enableFlap?: boolean;

  private baseWidthPx: number = 1126;
  private maxWidthPx: number = 1440;
  private growDistancePx: number = 250;

  @Element() private hostEl!: HTMLElement;

  private prefersReducedMotion = false;
  private rafPending = false;

  private startMarginPx: number = 0;
  private isMobileMatch = false;

  private onScroll = () => {
    if (!this.rafPending) {
      this.rafPending = true;
      requestAnimationFrame(this.updateFlapWidth);
    }
  };

  private onResize = () => {
    this.recomputeStartMargin();
    this.onScroll();
  };

  public componentWillLoad(): void {
    this.setFlapWidth(this.baseWidthPx);

    this.prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;

    this.hostEl.style.setProperty("--pf-flap-margin-x", `0px`);
  }

  public componentDidLoad(): void {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize);

    this.recomputeStartMargin();

    requestAnimationFrame(this.updateFlapWidth);
  }

  public disconnectedCallback(): void {
    window.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
  }

  private clamp01(n: number): number {
    return Math.max(0, Math.min(1, n));
  }

  private setFlapWidth(px: number): void {
    const clamped = Math.min(Math.max(px, 0), this.maxWidthPx);
    this.hostEl.style.setProperty("--pf-flap-width-px", `${clamped}px`);
  }

  private recomputeStartMargin(): void {
    const mq = window.matchMedia?.("(max-width: 768px)");
    this.isMobileMatch = mq ? mq.matches : window.innerWidth <= 768;

    if (this.isMobileMatch) {
      this.startMarginPx = 17;
    } else {
      this.startMarginPx = 0;
    }
  }

  private updateFlapWidth = (): void => {
    this.rafPending = false;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const progress = this.clamp01(scrollY / this.growDistancePx);

    if (this.prefersReducedMotion) {
      this.setFlapWidth(this.baseWidthPx);
      this.hostEl.style.setProperty(
        "--pf-flap-margin-x",
        `${this.startMarginPx}px`,
      );
      return;
    }

    const width =
      this.baseWidthPx + (this.maxWidthPx - this.baseWidthPx) * progress;
    this.setFlapWidth(width);

    // Update mobile-only gutter: start at startMarginPx and collapse to 0 with scroll
    const currentMarginPx = this.isMobileMatch
      ? Math.max(0, Math.round(this.startMarginPx * (1 - progress)))
      : 0;

    this.hostEl.style.setProperty("--pf-flap-margin-x", `${currentMarginPx}px`);
  };

  private renderText(
    text: string | undefined,
    size?: string,
    as: keyof HTMLElementTagNameMap = "h2",
    extraClass?: string | string[] | { [cls: string]: boolean },
  ) {
    const raw = typeof text === "string" ? text : "";
    if (!raw) return null;

    const normalized = this.normalizeMultiline(raw);
    const lines = normalized.split("\n");

    const classMap: { [cls: string]: boolean } = {};

    if (size && size.trim()) classMap[size.trim()] = true;

    if (extraClass) {
      if (typeof extraClass === "string") {
        extraClass
          .trim()
          .split(/\s+/)
          .forEach(c => {
            if (c) classMap[c] = true;
          });
      } else if (Array.isArray(extraClass)) {
        extraClass.forEach(c => {
          const cls = (c || "").trim();
          if (cls) classMap[cls] = true;
        });
      } else {
        Object.entries(extraClass).forEach(([cls, enabled]) => {
          if (enabled) classMap[cls] = true;
        });
      }
    }

    const children: any[] = [];
    lines.forEach((line, i) => {
      children.push(line);
      if (i < lines.length - 1) {
        children.push(h("br", { key: `br-${i}` }));
      }
    });

    return h(
      as as any,
      { class: Object.keys(classMap).length ? classMap : undefined },
      children,
    );
  }

  private normalizeMultiline(input: string | undefined): string {
    if (typeof input !== "string") return "";
    return input
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/\\r\\n|\\n|\\r/g, "\n")
      .replace(/\r\n|\r|\n/g, "\n");
  }

  private getParaflapClasses(): string {
    return "pf-wrapper";
  }

  private getFlapClasses(): string {
    return "para-flap";
  }

  render() {
    return (
      <Host>
        <section class={this.getParaflapClasses()}>
          <scs-hero-area card-data={this.heroCardData}></scs-hero-area>
          {this.enableFlap && (
            <div class={this.getFlapClasses()}>
              <div class={"pf-content"}>
                {this.renderText(
                  this.flapTitle,
                  this.flapTitleSize ? this.flapTitleSize : "d2",
                  "h1",
                )}
                {this.renderText(
                  this.flapSubTitle,
                  "h4",
                  "p",
                  "font--semi-light",
                )}
              </div>
            </div>
          )}
        </section>
      </Host>
    );
  }
}
